/**
 * PatternTable Component
 *
 * Displays tell→pitch type mappings in a sortable table.
 * Clicking a row filters thumbnails to that pattern.
 *
 * @module components/prediction/PatternTable
 */

import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ArrowUpRight, AlertCircle, CheckCircle } from 'lucide-react';
import { CONFIDENCE_LEVEL_COLORS } from '../../lib/predictionService';

// ═══════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sort indicator for table headers
 */
function SortIndicator({ active, direction }) {
  if (!active) return null;

  return direction === 'asc' ? (
    <ChevronUp className="w-4 h-4" />
  ) : (
    <ChevronDown className="w-4 h-4" />
  );
}

/**
 * Exploitability bar visualization
 */
function ExploitabilityBar({ value }) {
  const percentage = Math.round(value * 100);
  const barColor = percentage >= 70 ? 'bg-red-500' :
                   percentage >= 50 ? 'bg-orange-500' :
                   percentage >= 30 ? 'bg-yellow-500' : 'bg-gray-500';

  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm font-medium">{percentage}%</span>
    </div>
  );
}

/**
 * Confidence badge
 */
function ConfidenceBadge({ confidence }) {
  const percentage = Math.round(confidence * 100);
  const level = percentage >= 85 ? 'CRITICAL' :
                percentage >= 70 ? 'HIGH' :
                percentage >= 50 ? 'MEDIUM' : 'LOW';

  const colors = CONFIDENCE_LEVEL_COLORS[level];

  return (
    <span className={`px-2 py-1 rounded text-xs font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
      {percentage}%
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PatternTable - Tell→Pitch type mapping table
 *
 * @param {Object} props
 * @param {Array} props.patterns - Array of pattern objects
 * @param {Function} props.onPatternClick - Callback when pattern row is clicked
 * @param {string} props.selectedPattern - Currently selected pattern tell_type
 */
export default function PatternTable({
  patterns = [],
  onPatternClick,
  selectedPattern = null
}) {
  const [sortField, setSortField] = useState('exploitability');
  const [sortDirection, setSortDirection] = useState('desc');

  // Sort patterns
  const sortedPatterns = useMemo(() => {
    return [...patterns].sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;

      if (sortDirection === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  }, [patterns, sortField, sortDirection]);

  // Handle column header click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Table header cell component
  const HeaderCell = ({ field, label, sortable = true }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-500 ${
        sortable ? 'cursor-pointer hover:text-white transition-colors' : ''
      }`}
      onClick={sortable ? () => handleSort(field) : undefined}
      aria-sort={sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortable && <SortIndicator active={sortField === field} direction={sortDirection} />}
      </div>
    </th>
  );

  if (patterns.length === 0) {
    return (
      <div className="bg-black border border-white/[0.08] rounded-xl p-8 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-gray-500 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No Patterns Detected</h3>
        <p className="text-gray-400 text-sm">
          This pitcher has excellent mechanics! No obvious tells detected yet.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-black border border-white/[0.08] rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/[0.08]">
            <tr>
              <HeaderCell field="tell_type" label="Tell" sortable={false} />
              <HeaderCell field="pitch_types_affected" label="Pitch Type(s)" sortable={false} />
              <HeaderCell field="confidence" label="Confidence" />
              <HeaderCell field="occurrences" label="Occurrences" />
              <HeaderCell field="exploitability" label="Exploitability" />
              <th className="px-4 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.05]">
            {sortedPatterns.map((pattern, idx) => {
              const isSelected = selectedPattern === pattern.tell_type;
              const isOmitted = pattern.action === 'OMIT';

              return (
                <tr
                  key={pattern.tell_type || idx}
                  onClick={() => onPatternClick?.(pattern)}
                  className={`
                    cursor-pointer transition-all duration-200
                    ${isSelected ? 'bg-orange-500/10 border-l-2 border-orange-500' : 'hover:bg-white/5'}
                    ${isOmitted ? 'opacity-50' : ''}
                  `}
                >
                  {/* Tell Type */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      {isOmitted ? (
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                      <div>
                        <div className="font-semibold text-white">
                          {pattern.tell_type?.replace(/_/g, ' ')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {pattern.category?.label || pattern.category?.id || 'Other'}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Pitch Types Affected */}
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {pattern.pitch_types_affected?.map(pt => (
                        <span
                          key={pt}
                          className="px-2 py-0.5 bg-white/10 rounded text-xs font-medium text-white"
                        >
                          {pt}
                        </span>
                      )) || (
                        <span className="text-gray-500 text-sm">—</span>
                      )}
                    </div>
                  </td>

                  {/* Confidence */}
                  <td className="px-4 py-4">
                    <ConfidenceBadge confidence={pattern.confidence} />
                  </td>

                  {/* Occurrences */}
                  <td className="px-4 py-4">
                    <span className="text-white font-medium">
                      {pattern.occurrences || pattern.sample_count || 0}
                    </span>
                  </td>

                  {/* Exploitability */}
                  <td className="px-4 py-4">
                    <ExploitabilityBar value={pattern.exploitability} />
                  </td>

                  {/* Action */}
                  <td className="px-4 py-4">
                    <ArrowUpRight className="w-4 h-4 text-gray-500" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t border-white/[0.08] bg-white/[0.02]">
        <div className="flex items-center gap-6 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>Confirmed Pattern</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-500" />
            <span>False Positive (Omit)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
